import { ExtensionsRegistry, ExtensionMessageCollector } from './vscode/vs/workbench/services/extensions/common/extensionsRegistry.js';
import { toExtensionDescription } from './vscode/vs/workbench/services/extensions/common/extensions.js';
import { generateUuid } from 'monaco-editor/esm/vs/base/common/uuid.js';
import { URI } from 'monaco-editor/esm/vs/base/common/uri.js';
import { IExtHostExtensionService } from './vscode/vs/workbench/api/common/extHostExtensionService.js';
import { StandaloneServices } from 'monaco-editor/esm/vs/editor/standalone/browser/standaloneServices.js';
import { getExtensionId } from './vscode/vs/platform/extensionManagement/common/extensionManagementUtil.js';
import Severity from 'monaco-editor/esm/vs/base/common/severity.js';
import { localizeManifest } from './vscode/vs/platform/extensionManagement/common/extensionNls.js';
import { joinPath, originalFSPath } from 'monaco-editor/esm/vs/base/common/resources.js';
import { FileAccess } from 'monaco-editor/esm/vs/base/common/network.js';
import { ExtensionMode, ExtensionKind } from './vscode/vs/workbench/api/common/extHostTypes.js';
import { ExtensionGlobalMemento, ExtensionMemento } from './vscode/vs/workbench/api/common/extHostMemento.js';
import * as path from 'monaco-editor/esm/vs/base/common/path.js';
import { IFileService } from 'monaco-editor/esm/vs/platform/files/common/files.js';
import * as api from './api.js';
import { registerExtensionFile } from './service-override/files.js';
import create$7 from './vscode-services/l10n.js';
import create$6 from './vscode-services/languages.js';
import create$3 from './vscode-services/commands.js';
import create$5 from './vscode-services/workspace.js';
import create$4 from './vscode-services/window.js';
import create$2 from './vscode-services/env.js';
import create$1 from './vscode-services/debug.js';
import create from './vscode-services/extensions.js';
import { initialize as initialize$1, getExtHostServices } from './vscode-services/extHost.js';
export { onExtHostInitialized } from './vscode-services/extHost.js';
import { unsupported } from './tools.js';
import { setDefaultExtension } from './default-extension.js';
import './vscode/vs/workbench/contrib/search/browser/search.contribution.js';
function consoleExtensionMessageHandler(msg) {
    if (msg.type === Severity.Error) {
        console.error(msg);
    }
    else if (msg.type === Severity.Warning) {
        console.warn(msg);
    }
    else {
        console.log(msg);
    }
}
async function initialize(extension) {
    if (extension != null) {
        setDefaultExtension(extension);
    }
    await initialize$1();
}
function createApi(extension) {
    const workspace = create$5(() => extension);
    return {
        ...api,
        extensions: create(() => extension),
        debug: create$1(() => extension),
        env: create$2(() => extension),
        commands: create$3(() => extension),
        window: create$4(() => extension, workspace),
        workspace: create$5(() => extension),
        languages: create$6(() => extension),
        l10n: create$7(() => extension)
    };
}
const hasOwnProperty = Object.hasOwnProperty;
function handleExtensionPoint(extensionPoint, availableExtensions, messageHandler) {
    const users = [];
    for (const desc of availableExtensions) {
        if ((desc.contributes != null) && hasOwnProperty.call(desc.contributes, extensionPoint.name)) {
            users.push({
                description: desc,
                value: desc.contributes[extensionPoint.name],
                collector: new ExtensionMessageCollector(messageHandler, desc, extensionPoint.name)
            });
        }
    }
    extensionPoint.acceptUsers(users);
}
function deltaExtensions(toAdd, toRemove) {
    void StandaloneServices.get(IExtHostExtensionService).getExtensionRegistry().then(extensionRegistry => {
        const affectedExtensions = [].concat(toAdd).concat(toRemove);
        const affectedExtensionPoints = Object.create(null);
        for (const extensionDescription of affectedExtensions) {
            for (const extPointName in extensionDescription.contributes) {
                if (hasOwnProperty.call(extensionDescription.contributes, extPointName)) {
                    affectedExtensionPoints[extPointName] = true;
                }
            }
        }
        extensionRegistry.deltaExtensions(toAdd, ( toRemove.map(ext => ext.identifier)));
        const availableExtensions = extensionRegistry.getAllExtensionDescriptions();
        const extensionPoints = ExtensionsRegistry.getExtensionPoints();
        for (const extensionPoint of extensionPoints) {
            if (affectedExtensionPoints[extensionPoint.name] ?? false) {
                handleExtensionPoint(extensionPoint, availableExtensions, consoleExtensionMessageHandler);
            }
        }
    });
}
const extensionFileBlobUrls = new Map();
function registerExtensionFileBlob(extensionLocation, filePath, content, mimeType) {
    const blob = new Blob([content instanceof Uint8Array ? content : new TextEncoder().encode(content)], {
        type: mimeType
    });
    extensionFileBlobUrls.set(( joinPath(extensionLocation, filePath).toString()), URL.createObjectURL(blob));
}
const original = FileAccess.uriToBrowserUri;
FileAccess.uriToBrowserUri = function (uri) {
    if (uri.scheme === 'extension') {
        const extensionFile = extensionFileBlobUrls.get(( uri.toString()));
        if (extensionFile != null) {
            return ( URI.parse(extensionFile));
        }
    }
    return original.call(this, uri);
};
async function createExtensionContext(extensionDescription) {
    const { extHostStorage, extHostInitData } = getExtHostServices();
    const globalState = new ExtensionGlobalMemento(extensionDescription, extHostStorage);
    const workspaceState = new ExtensionMemento(extensionDescription.identifier.value, false, extHostStorage);
    await Promise.all([
        globalState.whenReady,
        workspaceState.whenReady
    ]);
    return {
        globalState,
        workspaceState,
        get secrets() { return unsupported(); },
        subscriptions: [],
        get extensionUri() { return extensionDescription.extensionLocation; },
        get extensionPath() { return extensionDescription.extensionLocation.fsPath; },
        asAbsolutePath(relativePath) { return path.join(extensionDescription.extensionLocation.fsPath, relativePath); },
        get storagePath() { return unsupported(); },
        get globalStoragePath() { return unsupported(); },
        get logPath() { return path.join(extHostInitData.logsLocation.fsPath, extensionDescription.identifier.value); },
        get logUri() { return URI.joinPath(extHostInitData.logsLocation, extensionDescription.identifier.value); },
        get storageUri() { return unsupported(); },
        get globalStorageUri() { return unsupported(); },
        extensionMode: ExtensionMode.Production,
        extension: {
            activate: unsupported,
            id: extensionDescription.identifier.value,
            extensionUri: extensionDescription.extensionLocation,
            extensionPath: path.normalize(originalFSPath(extensionDescription.extensionLocation)),
            isActive: true,
            packageJSON: extensionDescription,
            extensionKind: ExtensionKind.UI,
            exports: null
        },
        get environmentVariableCollection() { return unsupported(); }
    };
}
function runExtensionCode(api, code) {
    const initFn = new Function('module', 'exports', 'require', code);
    const _exports = {};
    const _module = { exports: _exports };
    const _require = (request) => {
        if (request === 'vscode') {
            return api;
        }
        throw new Error(`Cannot load module '${request}'`);
    };
    initFn(_module, _exports, _require);
    const module = (_module.exports !== _exports ? _module.exports : _exports);
    return module;
}
function registerExtension(manifest, defaultNLS) {
    const uuid = generateUuid();
    const location = ( URI.from({ scheme: 'extension', path: `/${uuid}` }));
    const localizedManifest = defaultNLS != null ? localizeManifest(manifest, defaultNLS) : manifest;
    const extension = {
        manifest: localizedManifest,
        type: 1 ,
        isBuiltin: false,
        identifier: {
            id: getExtensionId(localizedManifest.publisher, localizedManifest.name),
            uuid
        },
        location,
        targetPlatform: "web" ,
        isValid: true,
        validations: []
    };
    const extensionDescription = toExtensionDescription(extension);
    deltaExtensions([extensionDescription], []);
    const api = createApi(extensionDescription);
    return {
        api,
        async runCode() {
            if (extension.manifest.browser == null) {
                return;
            }
            let module = joinPath(location, extension.manifest.browser);
            module = module.with({ path: ensureSuffix(module.path, '.js') });
            const content = ( (await StandaloneServices.get(IFileService).readFile(module)).value.toString());
            const extensionModule = runExtensionCode(api, content);
            const context = await createExtensionContext(extensionDescription);
            await extensionModule.activate?.(context);
        },
        registerFile: (path, getContent) => {
            return registerExtensionFile(location, path, getContent);
        },
        registerSyncFile: (path, content, mimeType) => {
            registerExtensionFileBlob(location, path, content, mimeType);
            return registerExtensionFile(location, path, async () => content);
        },
        dispose() {
            deltaExtensions([], [extensionDescription]);
        }
    };
}
function ensureSuffix(path, suffix) {
    return path.endsWith(suffix) ? path : path + suffix;
}
export { consoleExtensionMessageHandler, createApi, initialize, registerExtension, runExtensionCode };
